import React, { SetStateAction, useEffect, useState } from "react";
import {
  Box,
  Checkbox,
  Typography,
  Button,
  Paper,
  Stack,
  useTheme,
  Tooltip,
  Select,
  MenuItem,
} from "@mui/material";
import { useDispatch , useSelector } from "react-redux";
import { KNOWLEDGE_BASE, MODEL_EMBEDDERS} from "../../../Services/Endpoint";
import BaseInstance from "../../../Services/AxiosInstance/BaseInstance";
import {
  StartLoading,
  StopLoading,
} from "../../../Shared-ui/Store/LoadingReducer/LoaderActions";
import { ShowError, ShowInfo } from "../../../Shared-ui/Store/MessageReducer/MsgActions";
import { KnowledgeBase } from "../../../Shared-ui/Types/KnowledgeBase";
import { Agent } from "../../../Shared-ui/Types/Agent";
import EmptyStateImage from "../../../assets/images/Agents_emptystateimage.svg";
import SearchIcon from "@mui/icons-material/Search";
import TextField from "@mui/material/TextField";
import { useNavigate } from "react-router-dom";
import variables from "../../../assets/styles/variables.module.scss";
import { kn } from "date-fns/locale";
import { FALSE } from "sass";

interface MyComponentProps {
  Agent: Agent;
  setAgent: React.Dispatch<SetStateAction<Agent>>;
  AgentErr: any;
}

const KnowledgeTab: React.FC<MyComponentProps> = (props) => {
  const [checked, setChecked] = React.useState<number[]>([]);
  const theme = useTheme();
  const dispatch = useDispatch();
  const [knowledgeBases, setKnowledgeBases] = useState<KnowledgeBase[]>([]);
  const loaderStatus = useSelector((state: any) => state.LoaderReducer);
  const [embedders, setEmbedders] = useState<any>([]);
  const [selectedEmbedder, setSelectedEmbedder] = useState<string>("");
  const [embedderBindedKB, setEmbedderBindedKB] = useState<KnowledgeBase[]>([]);
  const [selectedKbCount, setSelectedKbCount] = useState<number>(0)

  useEffect(() => {
    getKnowledgeBases(props?.Agent?.model?.split(":")[0] || "openai");
  }, []);

  useEffect(() => {
    getAllEmbedders();
    
  }, []);

  const getSelectedKbCount = () => {
    return knowledgeBases.filter(kb => kb.id !== undefined && 
                                                     props.Agent.knowledge_base_ids.includes(kb.id)).length
  }

  const navigate = useNavigate();

  const getKnowledgeBases = async (agent_embedder_model: string) => {
    try {
      dispatch(StartLoading("KnowledgeBase"));
      const data = await BaseInstance.get(`${KNOWLEDGE_BASE}/get-agent-embedder-kb/${agent_embedder_model}`)
      setKnowledgeBases(data?.data?.items || []);
    } catch (err: any) {
      dispatch(ShowError(err?.response?.data?.detail));
    } finally {
      dispatch(StopLoading("KnowledgeBase"));
    }
  };

  const getAllEmbedders = async () => {
      try {
        const response = await BaseInstance.get(MODEL_EMBEDDERS);
        setEmbedders(response?.data?.embedders);
        if(response?.data?.embedders?.length > 0){
          const defaultEmbedder = response.data.embedders.find((item: any) => item.provider.toLowerCase() === props?.Agent?.model.split(":")[0].toLowerCase());
          setSelectedEmbedder(defaultEmbedder?.embedder || "");
      }
      } catch (error: any) {
        console.error("Error fetching embedders:", error);
        dispatch(ShowError(error.message || "Failed to fetch embedders"));
      }
    };

  const handleSelectKW = (knowledge: KnowledgeBase) => {
    props.setAgent((preVal) => {
      const knowledgeBaseIds = preVal.knowledge_base_ids;
      if (knowledge.id) {
        const newKwIds = knowledgeBaseIds.includes(knowledge.id)
          ? knowledgeBaseIds.filter((id) => id !== knowledge.id)
          : [...knowledgeBaseIds, knowledge.id];

        return {
          ...preVal,
          knowledge_base_ids: newKwIds,
        };
      } else {
        return {
          ...preVal,
          knowledge_base_ids: knowledgeBaseIds,
        };
      }
    });
  };

  const handleClearAll = () => {
    props.setAgent((preVal) => {
      return {
        ...preVal,
        knowledge_base_ids: [],
      };
    });
  };

  const handleEmbedderFilter = (event: any) => {
    const embedder_obj = embedders.find((item: any) => item.embedder.toLowerCase() === event.target.value.toLowerCase())
    if(embedder_obj.provider.toLowerCase() !== props.Agent.model.split(":")[0].toLocaleLowerCase()){
      dispatch(ShowInfo("Agent and embedding model providers are different"))
    }
    setSelectedEmbedder(event.target.value);
    getKnowledgeBases(embedder_obj.provider.toLowerCase());
  }

  const [searchKnowledge, setSearchKnowledge] = useState<string>("");
  const isKnowledgeLoading = loaderStatus.isLoading && loaderStatus.pageName === "KnowledgeBase";
  const noKnowledgeFound = false;


  return (
    <Box sx={{}}>
      <Typography
        variant="subtitle2"
        sx={{ display: { xs: "block", md: "none" }, mb: 2, mt: 1 }}
      >
        Knowledge
      </Typography>
      <Box p={theme.spacing(0)} sx={{ maxWidth: "100%" }}>
        {isKnowledgeLoading || noKnowledgeFound ? (
          <Box
            sx={{
              display: "flex",
              flexDirection: "column",
              alignItems: "center",
              justifyContent: "center",
              mt: 6,
              cursor: isKnowledgeLoading ? "default" : "pointer",
            }}
          >
            <Tooltip title={isKnowledgeLoading ? "Knowledge base is loading..." : "No knowledge base found"} arrow>
              <img
                src={EmptyStateImage}
                alt={isKnowledgeLoading ? "Knowledge loading" : "No Knowledge Found"}
                style={{ width: 120, height: "auto" }}
              />
            </Tooltip>
            <Typography
              variant="h6"
              sx={{
                mt: 2,
                textDecoration: "underline",
                cursor: "pointer",
                color: variables.basicColor,
              }}
              {...(noKnowledgeFound ? { onClick: () => navigate("/knowledge-bases") } : {})}
            >
              {isKnowledgeLoading
              ? "Knowledge loading..."
              : "Click here to Create Knowledge"}
            </Typography>
          </Box>
        ) : (
          <>

            {/* Styled Paper Wrapper */}
            <Paper variant="outlined" sx={{ p: 2, borderRadius: 2 }}>
              <Typography variant="subtitle2" mb={1}>
              Available Knowledge
            </Typography>
              <TextField
                placeholder="Search Knowledge"
                value={searchKnowledge}
                onChange={(e) => setSearchKnowledge(e.target.value)}
                fullWidth
                sx={{ mb: 1 }}
                InputProps={{
                  startAdornment: (
                    <SearchIcon sx={{ color: "text.secondary", mr: 1 }} />
                  ),
                }}
              />
              <Box>
                <Select
                  value={selectedEmbedder}
                  onChange={handleEmbedderFilter}
                >
                  {embedders.map((item: any, index: number) => (
                    <MenuItem key={index} value={item?.embedder}>
                      {item?.embedder}
                    </MenuItem>
                  ))}
                </Select>
              </Box>
              <Box
                sx={{
                  display: "flex",
                  justifyContent: "space-between",
                  alignItems: "center",
                  mb: 1,
                }}
              >
                <Typography variant="subtitle1" fontWeight="500">
                  {/* Selected Knowledge ({props.Agent.knowledge_base_ids.length}) */}
                  Selected Knowledge ({getSelectedKbCount()})
                </Typography>
                {/* Clear All Button */}
                <Stack direction="row" justifyContent="flex-end">
                  <Button
                    variant="text"
                    onClick={handleClearAll}
                    disabled={props.Agent.knowledge_base_ids.length === 0}
                    sx={{
                      textTransform: "capitalize",
                    }}
                  >
                    Clear All
                  </Button>
                </Stack>
              </Box>
              <Box
                sx={{
                  display: "grid",
                  gridTemplateColumns: {
                    xs: "1fr",
                    sm: "1fr 1fr",
                    md: "1fr 1fr 1fr",
                  },
                  gap: 2,
                }}
              >
                {
                  knowledgeBases.filter((knowledgeBase) =>
                    knowledgeBase.name
                      .toLowerCase()
                      .includes(searchKnowledge.toLowerCase())
                  ).map((knowledgeBase) => (
                    <React.Fragment key={knowledgeBase.id}>
                      {knowledgeBase.id && (
                        <Stack
                          direction="row"
                          alignItems="center"
                          spacing={1}
                          sx={{
                            cursor: "pointer",
                            border: "1px solid #ccc",
                            p: 1,
                            borderRadius: 1,
                            transition: "border-color 0.2s, background 0.2s",
                            "&:hover": {
                              borderColor: "primary.main",
                              background: "#f5faff",
                            },
                          }}
                          onClick={() => handleSelectKW(knowledgeBase)}
                        >
                          <Checkbox
                            checked={props.Agent.knowledge_base_ids.includes(
                              knowledgeBase.id
                            )}
                            onClick={(e) => e.stopPropagation()} // Prevent Stack's onClick when clicking the checkbox
                            onChange={() => handleSelectKW(knowledgeBase)}
                            disableRipple
                          />
                          <Typography
                            variant="body1"
                            sx={{
                              textTransform: "capitalize",
                            }}
                          >
                            {knowledgeBase.name}
                          </Typography>
                        </Stack>
                      )}
                    </React.Fragment>
                  ))}
              </Box>
             {knowledgeBases.length == 0 && <Box>
                <Typography
                    variant="h6"
                    sx={{
                      mt: 2,
                      textDecoration: "underline",  
                      cursor: "pointer",
                      color: variables.basicColor,
                    }}
                    {...({ onClick: () => navigate("/knowledge-bases") })}
                  >
                    {isKnowledgeLoading
                    ? "Knowledge loading..."
                    : "Click here to Create Knowledge"}
                </Typography>
              </Box>}
            </Paper>
          </>
        )}
      </Box>
    </Box>
  );
};

export default KnowledgeTab;
function dispatch(arg0: any) {
  throw new Error("Function not implemented.");
}
